import axios from 'axios'
import { axiosInstance } from './axios.service'
import { IResponse } from '../types/response.type'
import { RequestFormEndpoints } from '../types/endpoint.type'
import {
  IExercise,
  IUsageTime,
  IStateRegion,
  IMajorFunction,
  IRequest,
  ILocationTemplate,
  IUsageTimeService,
  IFrequencyTemplate,
  IIntermediateFunction,
  IRequestLocation,
  IRequestFrequencyGroup,
  IRequestFrequencyExclusion,
} from '../types/exercise.type'
import { IRequestFrequencyGroupReceiveLocation } from '../../shared/types/api/request-form.type'
import { IClassificationType } from '../types/classifications.type'
import { IOperatingSubUnit } from '../types/operating-unit.types'
import { IOperatingUnit } from '../../shared/types/api/user.type'

export const getAllExercises = async (): Promise<IResponse<IExercise[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_EXERCISES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getSingleRequest = async (id: number): Promise<IResponse<IRequest>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.GET_REQUESTS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllRequests = async (
  user_id: string | null,
  exercise_id: number | null,
  operating_unit_id: number | null,
): Promise<IResponse<IRequest[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_REQUESTS, {
      params: { exercise_id, user_id, operating_unit_id },
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllOperatingUnits = async (): Promise<IResponse<IOperatingUnit[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_OPERATING_UNITS)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllOperatingSubUnits = async (operating_unit: number): Promise<IResponse<IOperatingSubUnit[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.GET_OPERATING_SUB_UNITS}/${operating_unit}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllClassificationTypes = async (): Promise<IResponse<IClassificationType[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_CLASSIFICATION_TYPES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createRequest = async (exerciseId?: number): Promise<IResponse<IRequest>> => {
  try {
    const data = await axiosInstance.post(`${RequestFormEndpoints.REQUEST}`, {
      exerciseId: exerciseId || null,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

// Fields the saveRequestValidator accepts; everything else on an IRequest
// (id, tiers, facets, deleted, created_at, status, user_id, etc.) is server-
// or DB-managed and gets rejected as "not allowed".
const REQUEST_FIELDS: (keyof IRequest)[] = [
  'name',
  'username',
  'user_phone',
  'exercise_id',
  'classification_type_id',
  'start_date',
  'end_date',
  'tiers_by_level',
]

export const updateRequest = async (id: number, request: IRequest): Promise<IResponse<IRequest>> => {
  try {
    const body: Partial<IRequest> = {}
    for (const k of REQUEST_FIELDS) if (k in request) (body as any)[k] = request[k]
    const data = await axiosInstance.put(`${RequestFormEndpoints.REQUEST}/${id}`, body)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteRequest = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${RequestFormEndpoints.REQUEST}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllLocations = async (id: number): Promise<IResponse<IRequestLocation[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.REQUEST_LOCATIONS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createExerciseLocation = async (id: number): Promise<IResponse<IRequestLocation>> => {
  try {
    const data = await axiosInstance.post(RequestFormEndpoints.REQUEST_LOCATIONS, {
      exerciseRequestId: id,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateLocation = async (
  id: number,
  params: {
    rad?: number
    lon_EW?: string
    lat_NS?: string
    lon_deg?: number
    lon_min?: number
    lon_sec?: number
    lat_deg?: number
    lat_min?: number
    lat_sec?: number
    elevation?: number
    loc_template?: number
    transmitter_loc?: string
    state_region_id?: number
  },
): Promise<IResponse<IRequestLocation>> => {
  try {
    const data = await axiosInstance.put(`${RequestFormEndpoints.REQUEST_LOCATIONS}/${id}`, params)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteLocation = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${RequestFormEndpoints.REQUEST_LOCATIONS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getLocationElevation = async (lat: number, long: number): Promise<any> => {
  try {
    const data = await axios.get(`
      https://api.open-elevation.com/api/v1/lookup?locations=${lat},${long}
    `)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllLocationTemplates = async (): Promise<IResponse<ILocationTemplate[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_LOCATION_TEMPLATES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllStateRegions = async (): Promise<IResponse<IStateRegion[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_STATE_REGIONS)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllFrequencies = async (id: number): Promise<IResponse<IRequestFrequencyGroup[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.REQUEST_FREQUENCY_GROUPS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createFrequencyGroup = async (id: number): Promise<IResponse<IRequestFrequencyGroup>> => {
  try {
    const data = await axiosInstance.post(RequestFormEndpoints.REQUEST_FREQUENCY_GROUPS, {
      exerciseRequestId: id,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateFrequencyGroup = async (
  id: number,
  params: {
    hopset: string
    saturn: boolean
    platform: string
    locationId: number | null
    usageTimeId: number | null
    percentTime: number
    majorFunctionId: number | null
    usageTimeServiceId: number | null
    detailedFunction: string | null
    numberOfFrequencies: number
    supplementaryDetails: string
    intermediateFunctionId: number | null
    startFreq: number
    startIndicator: string | null
    endFreq: number
    endIndicator: string | null
    isCustom: boolean
    frequencyTemplateId: number | null
  },
): Promise<IResponse<IRequestFrequencyGroup>> => {
  try {
    const resp = await axiosInstance.put(`${RequestFormEndpoints.REQUEST_FREQUENCY_GROUPS}/${id}`, params)

    return resp.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteFrequencyGroup = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${RequestFormEndpoints.REQUEST_FREQUENCY_GROUPS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllMajorFunctions = async (): Promise<IResponse<IMajorFunction[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_MAJOR_FUNCTIONS)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getIntermediateFunctionsByMajorFunctionId = async (
  id: number,
): Promise<IResponse<IIntermediateFunction[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.GET_INTERMEDIATE_FUNCTIONS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllFrequencyExclusions = async (id: number): Promise<IResponse<IRequestFrequencyExclusion[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.REQUEST_FREQUENCY_EXCLUSIONS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createFrequencyExclusion = async (id: number): Promise<IResponse<IRequestFrequencyExclusion>> => {
  try {
    const data = await axiosInstance.post(`${RequestFormEndpoints.REQUEST_FREQUENCY_EXCLUSIONS}/${id}`, {})

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getRequestFrequencyGroupReceiveLocations = async (
  request_frequency_group_id: number,
): Promise<IResponse<IRequestFrequencyGroupReceiveLocation[]>> => {
  const resp = await axiosInstance.get(
    `/request-form/request-frequency-group/${request_frequency_group_id}/receive-location`,
  )
  return resp.data
}

export const saveRequestFrequencyGroupReceiveLocations = async (
  request_frequency_group_id: number,
  request_location_ids: number[],
): Promise<IResponse<IRequestFrequencyGroupReceiveLocation>> => {
  // Filter out duplicates:
  const ids = [...new Set(request_location_ids)]
  const resp = await axiosInstance.put(
    `/request-form/request-frequency-group/${request_frequency_group_id}/receive-locations`,
    ids.map((id) => ({ request_location_id: id })),
  )
  return resp.data
}

export const updateFrequencyExclusion = async (
  id: number,
  params: {
    start_freq: number
    start_indicator: string
    end_freq: number
    end_indicator: string
    is_band: boolean
  },
): Promise<IResponse<IRequestFrequencyExclusion>> => {
  try {
    const data = await axiosInstance.put(`${RequestFormEndpoints.REQUEST_FREQUENCY_EXCLUSIONS}/${id}`, params)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteFrequencyExclusion = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${RequestFormEndpoints.REQUEST_FREQUENCY_EXCLUSIONS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllFrequencyTemplates = async (): Promise<IResponse<IFrequencyTemplate[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_FREQUENCY_TEMPLATES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllUsageTimes = async (): Promise<IResponse<IUsageTime[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_USAGE_TIMES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllUsageTimeServices = async (): Promise<IResponse<IUsageTimeService[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_USAGE_TIME_SERVICES)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}
